import React, {
  useEffect, useMemo, useState, useRef, useCallback, Fragment, RefObject, ReactElement,
} from 'react';
import { useNavigate } from 'react-router';
import Card from '../../../components/card/card.component';
import Menu from '../../../components/menu/menu.component';
import Table from '../../../components/table/table.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import Grid from '../../../components/grid/grid.component';
import GridItem from '../../../components/grid-item/grid-item.component';
import Button from '../../../components/button/button.component';
import ModalButtonBlock from '../../../components/modal-button-block/modal-button-block.component';
import StatusBar from '../../../components/status-bar/status-bar.component';
import ModalLayout from '../../../components/modal-layout/modal-layout.component';
import RequiredMark from '../../../components/required-mark/required-mark.component';
import ActionButton from '../../../components/action-button/action-button.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import Loader from '../../../components/loader/loader.component';
import FilteredLink from '../../../components/filtered-link/filtered-link.component';
import ChipLink from '../../../components/chip-link/chip-link.component';
import FlexContainer from '../../../components/flex-container/flex-container.component';
import LeaveConfirmModal from '../../../components/leave-confirm-modal/leave-confirm-modal.component';
import FileLink from '../../../components/file-link/file-link.component';
import FileListContainer from '../../../components/file-list-container/file-list-container.component';
import ContextMenu from '../../../components/context-menu/context-menu.component';
import MenuItemAddNewItem from '../../../components/menu-item-add-new-item/menu-item-add-new-item.component';
import DropdownButton from '../../../components/dropdown-button/dropdown-button.component';
import TextBlock from '../../../components/text-block/text-block.component';
import Form from '../../../components/form/form.component';
import FormFieldSelect from '../../../components/form-field-select/form-field-select.component';
import FormFieldMultiSelect from '../../../components/form-field-multi-select/form-field-multi-select.component';
import FormFieldText from '../../../components/form-field-text/form-field-text.component';
import FormFieldDate from '../../../components/form-field-date/form-field-date.component';
import MenuItemList from '../../../components/menu-item-list/menu-item-list.component';
import CommentTable from '../../app/common/comment-table/comment-table.component';
import CommentForm from '../../app/common/comment-form/comment-form.component';
import ControlButtonBlock from '../../app/common/control-button-block/control-button-block.component';
import DealTaskStatusIcon from '../deal-task-status-icon/deal-task-status-icon.component';
import DealTaskReviewStatusIcon from '../deal-task-review-status-icon/deal-task-review-status-icon.component';
import CancelFilledIcon from '../../../icons/cancel-filled-icon';
import ChatAssistantIcon from '../../../icons/chat-assistant-icon';
import FormService from '../../../services/form-service';
import StructureService from '../../../services/structure-service';
import FormatService from '../../../services/format-service';
import CommentService from '../../../services/comment-service';
import useScreenSmall from '../../../hooks/use-screen-small';
import useCurrentUserPermissions from '../../../hooks/use-current-user-permissions';
import {
  KEYBOARD_KEY_ARROW_UP, KEYBOARD_KEY_ENTER, KEYBOARD_KEY_ESCAPE,
} from '../../../constants/system/keyboard-keys';
import {
  DEAL_TASK_ATTACHMENT_LIST_VIEW, DEAL_TASK_COMMENT_CREATE, DEAL_TASK_COMMENT_DELETE, DEAL_TASK_ACCESS_LEVEL_VIEW,
  DEAL_TASK_COMMENT_EDIT, DEAL_TASK_COMMENT_LIST_VIEW, DEAL_TASK_GROUP_CREATE, DEAL_TASK_EDIT, DEAL_TASK_STATUS_EDIT,
  CONTACT_VIEW, SEARCH_RESULT_VIEW, DEAL_FILE_TEXT_DATA_VIEW,
} from '../../../constants/entity/permissions';
import {
  DEAL_TASK_REVIEW_STATUS_APPROVED, DEAL_TASK_REVIEW_STATUS_PENDING,
} from '../../../constants/entity/deal-task-review-statuses';
import { TIMEOUT_DEFAULT } from '../../../constants/system/timeouts';
import { DEAL_TASK_STATUS_APPROVED } from '../../../constants/entity/deal-task-statuses';
import { ROUTE_CONTACT, ROUTE_SEARCH } from '../../../constants/system/routes';
import { HTML_TAG_NAME_ANCHOR } from '../../../constants/system/html-tag-names';
import { MIME_TYPE_APPLICATION_PDF } from '../../../constants/system/mime-types';
import { DATA_ATTACHMENT_ID, DATA_CONTACT_ID, DATA_IS_ANALYZABLE } from '../../../constants/system/data-attributes';
import { SIGN_HASH, SIGN_AT } from '../../../constants/system/signs';
import PersonDefaultImage from '../../../static/images/person-default-image.png';

const DealTaskModal: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const MODAL_TITLE_DEFAULT: string = 'New Task';
  const MODAL_TITLE_EDIT_PREFIX: string = 'Edit';

  const {
    isOpen,
    isSaving,
    isSaveDisabled,
    isCommentSaving,
    isEditMode,
    isNewDeal,
    isModalEditMode,
    isRightDrawerOpen,
    isNewEntity,
    isCommentFormActive,
    dealTaskData,
    dealTaskGroupData,
    currentUser,
    queryCommentId,
    dealTaskPlainList,
    dealTaskStatusList,
    accessLevelList,
    dealTaskContactPersonList,
    dealTaskGroupList,
    contactLinkedUserList,
    tagList,
    saveFormDeps,
    onCreate,
    onCancel,
    onCloseModal,
    onCancelForm,
    onSubmitForm,
    onAddComment,
    onUpdateComment,
    onDeleteComment,
    onCommentEntityLinkClick,
    onStatusChange,
    onReviewButtonClick,
    onCommentAttachmentFileViewClick,
    onCloseNewGroupForm,
    onDealTaskGroupChange,
    onDealTaskReviewContactsChange,
    onDealTaskContactPersonSearch,
    onDealTaskContactPersonSearchClose,
    onSwitchModalEditMode,
    onSubmitCreateForm,
    onOpenDealFileChatModal,
  } = props;

  const navigate: ReactRouterNavigate = useNavigate();
  const isSmallScreen: boolean = useScreenSmall();
  const permissionList: string[] = useCurrentUserPermissions();
  const contentContainerRef: RefObject<HTMLDivElement> | null = useRef(null);

  const parsedDueDate: Date = dealTaskData && dealTaskData.dueDate && new Date(dealTaskData.dueDate);
  const currentUserContactId: number | null = (currentUser && currentUser.contactId) || null;
  const currentUserOpenReview: DealTaskReview | null = dealTaskData && dealTaskData.reviews
  && dealTaskData.reviews.length && currentUserContactId
    ? dealTaskData.reviews.find((dealTaskReview: DealTaskReview): boolean => (
      dealTaskReview.contact.id === currentUserContactId && dealTaskReview.status === DEAL_TASK_REVIEW_STATUS_PENDING
    )) : null;
  const currentUserOpenReviewId: number | null = (currentUserOpenReview && currentUserOpenReview.id) || null;
  const isReviewRequired: boolean = !!currentUserOpenReviewId;
  const withEditModeSwitcher: boolean = !isNewEntity && !isNewDeal && !isEditMode;

  const [menuAnchor, setMenuAnchor]: [HTMLElement | null, Function] = useState(null);
  const [fileMenuAnchor, setFileMenuAnchor]: [HTMLElement | null, Function] = useState(null);
  const [commentText, setCommentText]: [string | null, Function] = useState(null);
  const [isOpenMessage, setIsOpenMessage]: [boolean, Function] = useState(false);
  const [isMessageError, setIsMessageError]: [boolean, Function] = useState(false);
  const [statusMessage, setStatusMessage]: [string, Function] = useState('');
  const [commentAttachments, setCommentAttachments]: [AttachmentInput[] | null, Function]
    = useState<StateValueDefault>(null);
  const [commentAttachmentFiles, setCommentAttachmentFiles]: [File[] | null, Function]
    = useState<StateValueDefault>(null);
  const [isNewGroupVisible, setIsNewGroupVisible]: [boolean, Function] = useState(false);
  const [commentEditId, setCommentEditId]: [number | null, Function] = useState(null);
  const [isLeaveConfirmModalOpen, setIsLeaveConfirmModalOpen]: [boolean, Function] = useState(false);
  const [commentActiveId, setCommentActiveId]: [number | null, Function] = useState(null);
  const [contentHeight, setContentHeight]: [number | null, Function] = useState(null);
  const [currentDealTaskGroupId, setCurrentDealTaskGroupId]: [number | null, Function] = useState(null);
  const [selectedStatus, setSelectedStatus]: [string | null, Function] = useState(null);
  const [selectedStatusText, setSelectedStatusText]: [string | null, Function] = useState(null);
  const [selectedDealTaskReviewContacts, setSelectedDealTaskReviewContacts]: [Contact[] | null, Function]
    = useState(null);

  const commentAttachmentDropDeps: DepsItem[] = useMemo((): DepsItem[] => [
    commentAttachmentFiles,
  ], [commentAttachmentFiles]);
  const commonAttachmentFiles: (AttachmentInput | File)[] = useMemo((): (AttachmentInput | File)[] => [
    ...(commentAttachments || []),
    ...(commentAttachmentFiles || []),
  ], [commentAttachments, commentAttachmentFiles]);

  const mentionList: MentionItem[] = useMemo((): MentionItem[] => (
    contactLinkedUserList && contactLinkedUserList.length
      ? contactLinkedUserList.map((contactItem: Contact): MentionItem => ({
        id: `${SIGN_AT}${contactItem.fullName}`,
        contactId: contactItem.id,
        fullName: contactItem.fullName,
        photo: contactItem.photo,
        photoThumbnail: contactItem.photoThumbnail,
      })) : []
  ), [contactLinkedUserList]);

  const hashtagList: HashtagItem[] = useMemo((): HashtagItem[] => (
    tagList && tagList.length ? tagList.map((tagItem: Tag): HashtagItem => ({
      id: `${SIGN_HASH}${tagItem.name}`,
      tagId: tagItem.id,
      name: tagItem.name,
    })) : []
  ), [tagList]);

  const dueDate: string = useMemo((): string => (
    parsedDueDate && FormatService.formatDateString(parsedDueDate)
  ), [parsedDueDate]);
  const filteredDealTaskPlainList: DealTask[] = useMemo((): DealTask[] => (
    dealTaskPlainList && dealTaskPlainList.filter((dealTaskItem: DealTask): boolean => (
      ((dealTaskData && dealTaskItem.id !== dealTaskData.id) || !dealTaskData) && !dealTaskItem.parentId
      && (currentDealTaskGroupId && currentDealTaskGroupId === dealTaskItem.groupId)
    ))
  ), [dealTaskPlainList, dealTaskData, currentDealTaskGroupId]);

  const modalTitle: string = useMemo((): string => {
    if (!isNewEntity && dealTaskData && dealTaskData.name) {
      return `${(isEditMode || isModalEditMode) ? `${MODAL_TITLE_EDIT_PREFIX} ` : ''}${dealTaskData.name}`;
    }

    return MODAL_TITLE_DEFAULT;
  }, [isNewEntity, isEditMode, isModalEditMode, dealTaskData]);

  const attachmentActionMenuPermissions: string[] = useMemo((): string[] => [DEAL_FILE_TEXT_DATA_VIEW], []);

  const dealTaskId: number = (!isNewEntity && dealTaskData && dealTaskData.id) || FormService.FORM_KEY_NEW;
  const dealTaskGroupId: number = (!isNewEntity && dealTaskGroupData && dealTaskGroupData.id)
    || FormService.FORM_KEY_NEW;
  const formKeyDealTask: string = FormService.getIndexedFormFieldKey(dealTaskId);
  const formKeyDealTaskGroup: string = FormService.getIndexedFormFieldKey(dealTaskGroupId);
  const commentList: Comment[] = (dealTaskData && dealTaskData.comments) || [];
  const attachmentList: Attachment[] = (dealTaskData && dealTaskData.attachments) || [];
  const formPrefix: string = `dealData.dealTasksData[${formKeyDealTaskGroup}].tasks[${formKeyDealTask}]`;
  const isDealTaskCommentListVisible: boolean = StructureService.checkPermission(
    DEAL_TASK_COMMENT_LIST_VIEW, permissionList,
  );
  const isFullWidthTable: boolean = isSmallScreen || isEditMode || isNewEntity || isNewDeal
    || isModalEditMode || !isDealTaskCommentListVisible;

  const dealTaskStatusEditPermissions: string[] = useMemo((): string[] => [DEAL_TASK_STATUS_EDIT], []);
  const dealTaskAccessLevelViewPermissions: string[] = useMemo((): string[] => [DEAL_TASK_ACCESS_LEVEL_VIEW], []);
  const dealTaskAttachmentListViewPermissions: string[] = useMemo((): string[] => [DEAL_TASK_ATTACHMENT_LIST_VIEW], []);
  const dealTaskAttachmentActionMenuPermissions: string[] = useMemo((): string[] => [DEAL_FILE_TEXT_DATA_VIEW], []);
  const dealTaskCommentListViewPermissions: string[] = useMemo((): string[] => [DEAL_TASK_COMMENT_LIST_VIEW], []);
  const dealTaskCommentEditPermissions: string[] = useMemo((): string[] => [DEAL_TASK_COMMENT_EDIT], []);
  const dealTaskCommentDeletePermissions: string[] = useMemo((): string[] => [DEAL_TASK_COMMENT_DELETE], []);
  const dealTaskCommentCreatePermissions: string[] = useMemo((): string[] => [DEAL_TASK_COMMENT_CREATE], []);

  const fileActionMenuItems: ActionMenuItem[] = useMemo((): ActionMenuItem[] => [
    {
      id: 1,
      icon: <ChatAssistantIcon />,
      text: 'Open File Chat',
      permission: DEAL_FILE_TEXT_DATA_VIEW,
      isVisibleByEntityTypeValue: FormatService.BOOLEAN_VALUE_TRUE,
      onClick: (event: React.MouseEvent<Element, MouseEvent>, attachmentId: number): void => {
        if (dealTaskData) {
          const dealTaskAttachment: Attachment | null = dealTaskData
            ? dealTaskData.attachments.find((attachmentItem: Attachment): boolean => (
            attachmentItem.id === attachmentId
          )) || null : null;

          if (onOpenDealFileChatModal && dealTaskAttachment) {
            onOpenDealFileChatModal(event, dealTaskAttachment, dealTaskData.id);
          }
        }
      },
    },
  ], [dealTaskData]);

  const openStatusMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setMenuAnchor(event.currentTarget);
  }, []);

  const closeStatusMenu: Function = useCallback((): void => {
    setMenuAnchor(null);
  }, []);

  const onCloseStatusMenu: MouseEventHandlerFunction = useCallback((): void => {
    closeStatusMenu();
  }, []);

  const onSaveCommentSuccess: Function = useCallback((commentId?: number): void => {
    const lastComment: Comment | null = commentList && commentList.length ? commentList[commentList.length - 1] : null;
    const resultCommentId: number | null = commentId || (lastComment && lastComment.id) || null;

    if (resultCommentId) {
      CommentService.scrollToComment(resultCommentId);
    }
  }, [commentList]);

  const saveComment: Function = useCallback((): void => {
    const mentionContactIds: number[] = commentText
      ? FormatService.extractHtmlAttributes(commentText, HTML_TAG_NAME_ANCHOR, DATA_CONTACT_ID, true) as number[] : [];

    if (commentEditId) {
      if (onUpdateComment && (commentText || (commentAttachments && commentAttachments.length)
        || (commentAttachmentFiles && commentAttachmentFiles.length))) {
        onUpdateComment(
          dealTaskId, commentText, commentAttachments, commentAttachmentFiles, commentEditId,
          mentionContactIds, onSaveCommentSuccess,
        );
      }
      setCommentEditId(null);
    } else if (onAddComment && (commentText || (commentAttachmentFiles && commentAttachmentFiles.length))) {
      onAddComment(dealTaskId, commentText, commentAttachmentFiles, mentionContactIds, onSaveCommentSuccess);
    }

    setCommentText(null);
    setCommentAttachmentFiles(null);
    setCommentAttachments(null);
  }, [commentText, commentAttachmentFiles, commentAttachments, commentEditId, dealTaskId, commentList]);

  const onCommentFieldChange: Function = useCallback((event: React.ChangeEvent, commentTextValue: string): void => {
    setCommentText(commentTextValue);
  }, []);

  const onCommentFieldKeyDown: Function = useCallback((event: React.KeyboardEvent, commentTextValue: string): void => {
    if (event.key === KEYBOARD_KEY_ARROW_UP && !commentTextValue) {
      CommentService.editLastComment();

      event.preventDefault();
    }

    if (event.key === KEYBOARD_KEY_ESCAPE) {
      onCancelCommentForm();
    }

    if (event.key === KEYBOARD_KEY_ENTER && !event.shiftKey) {
      setCommentText(commentTextValue);

      CommentService.sendComment();
    }
  }, []);

  const cancelForm: Function = useCallback((): void => {
    setCommentAttachmentFiles(null);
    setCommentAttachments(null);
    setCommentActiveId(null);
    setCommentEditId(null);
    setCommentText(null);

    setIsNewGroupVisible(false);
    setCurrentDealTaskGroupId(null);
    setSelectedDealTaskReviewContacts(null);
  }, []);

  const cancelChangesAndCloseModal: MouseEventHandlerFunction = useCallback((): void => {
    if (selectedStatus) {
      setSelectedStatus(null);
      setSelectedStatusText(null);
    }

    if (onCancel && !isEditMode) {
      onCancel();
    } else if (onCloseModal) {
      onCloseModal();
    }

    cancelForm();
  }, [selectedStatus, isEditMode]);

  const applyChangesAndCloseModal: MouseEventHandlerFunction = useCallback((): void => {
    onSubmitCreateForm((): void => {
      if (selectedStatus) {
        if (onStatusChange) {
          onStatusChange(dealTaskId, selectedStatus);
        }
        setSelectedStatus(null);
        setSelectedStatusText(null);
      }

      if ((commentText || (commentAttachmentFiles && commentAttachmentFiles.length)) && dealTaskId) {
        saveComment();
      }
      if (selectedStatus || commentText || (commentAttachmentFiles && commentAttachmentFiles.length)) {
        setStatusMessage('Saved successfully');
        setIsOpenMessage(true);
        setIsMessageError(false);
      }

      if (isNewEntity && onCreate) {
        onCreate();
      } else if (onCloseModal) {
        onCloseModal();
      }

      cancelForm();
    });
  }, [
    isSaveDisabled, selectedStatus, dealTaskId, commentText, commentAttachmentFiles, isNewEntity, isEditMode,
    ...(saveFormDeps || []),
  ]);

  const onStatusSelect: Function = useCallback((statusValue: string): void => {
    if (!isEditMode && !isNewEntity && !isNewDeal && !isModalEditMode) {
      dealTaskStatusList.forEach((dealTaskStatusItem: DealTaskStatus): void => {
        if (dealTaskStatusItem.name === statusValue) {
          setSelectedStatus(dealTaskStatusItem.name);
          setSelectedStatusText(dealTaskStatusItem.title);
        }
      });
      closeStatusMenu();
    }
  }, [isEditMode, isNewEntity, isNewDeal, isModalEditMode]);

  const closeMessage: Function = useCallback((): void => {
    setIsOpenMessage(false);
    setIsMessageError(false);
    setStatusMessage('');
  }, []);

  const onCommentAttachmentFileDelete: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, fileIndex: number,
  ): void => {
    const isCommentEditing: boolean = !!commentEditId;
    const currentCommentAttachmentFiles: File[] = [...(commentAttachmentFiles || [])];
    const currentCommentAttachments: AttachmentInput[] = [...(commentAttachments || [])];

    if (fileIndex !== null && fileIndex >= 0) {
      if (isCommentEditing && fileIndex < currentCommentAttachments.length) {
        const newCommentAttachments: AttachmentInput[] = currentCommentAttachments;
        StructureService.deleteListItemByIndex(newCommentAttachments, fileIndex);
        setCommentAttachments(newCommentAttachments);
      } else {
        const newCommentAttachmentFiles: File[] = currentCommentAttachmentFiles;
        StructureService.deleteListItemByIndex(
          newCommentAttachmentFiles,
          fileIndex - (isCommentEditing ? currentCommentAttachments.length : 0),
        );
        setCommentAttachmentFiles(newCommentAttachmentFiles);
      }
    }
  }, [commentAttachmentFiles, commentAttachments]);

  const onCommentAttachmentFilesChange: Function = useCallback((files: File[]): void => {
    setCommentAttachmentFiles([...(commentAttachmentFiles || []), ...files]);
  }, [commentAttachmentFiles]);

  const onCommentAttachmentFileError: Function = useCallback((error: string): void => {
    setStatusMessage(error);
    setIsOpenMessage(true);
    setIsMessageError(true);
  }, []);

  const openNewGroupForm: Function = useCallback((): void => {
    setIsNewGroupVisible(true);

    if (onDealTaskGroupChange) {
      onDealTaskGroupChange(dealTaskId, dealTaskGroupId);
    }
  }, [dealTaskId, dealTaskGroupId]);

  const closeNewGroupForm: MouseEventHandlerFunction = useCallback((): void => {
    setIsNewGroupVisible(false);

    if (onCloseNewGroupForm) {
      onCloseNewGroupForm(dealTaskId, dealTaskGroupId);
    }
  }, [dealTaskId, dealTaskGroupId]);

  const switchCommentEditMode: Function = useCallback((event: React.SyntheticEvent, comment: Comment): void => {
    const isEditModeActive: boolean = !commentEditId || commentEditId !== comment.id;

    setCommentEditId(isEditModeActive ? comment.id : null);
    setCommentText(isEditModeActive ? (comment.text || '') : null);

    const commentAttachmentItems: Attachment[] | null = comment.attachments && comment.attachments.length
      ? comment.attachments : null;
    setCommentAttachments(isEditModeActive && commentAttachmentItems
      ? commentAttachmentItems.map((attachmentItem: Attachment): AttachmentInput => ({
        id: attachmentItem.id,
        name: attachmentItem.file.name,
        link: attachmentItem.file.linkName,
      })) : null);
    setCommentAttachmentFiles(isEditModeActive ? [] : null);

    event.stopPropagation();
  }, [commentEditId, commentList]);

  const dealTaskGroupChange: Function = useCallback((dealTaskGroupIdValue: number): void => {
    setCurrentDealTaskGroupId(dealTaskGroupIdValue);

    if (onDealTaskGroupChange) {
      onDealTaskGroupChange(dealTaskId, dealTaskGroupId);
    }
  }, [dealTaskId, dealTaskGroupId]);

  const dealTaskReviewContactsChange: Function = useCallback((dealTaskReviewContactValues: Contact[]): void => {
    if (onDealTaskReviewContactsChange) {
      onDealTaskReviewContactsChange(dealTaskId, dealTaskGroupId, dealTaskReviewContactValues);
    }

    setSelectedDealTaskReviewContacts(dealTaskReviewContactValues);
  }, [dealTaskId, dealTaskGroupId]);

  const onDeleteCommentCancel: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, commentId: number,
  ): void => {
    if (commentActiveId === commentId) {
      setCommentActiveId(null);
    }

    event.stopPropagation();
  }, [commentActiveId]);

  const onCancelCommentForm: Function = useCallback((): void => {
    setCommentAttachmentFiles(null);
    setCommentAttachments(null);
    setCommentActiveId(null);
    setCommentEditId(null);
    setCommentText(null);
  }, []);

  const onDeleteCommentConfirm: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, commentId: number,
  ): void => {
    if (onDeleteComment) {
      onDeleteComment(dealTaskId, commentId, onSaveCommentSuccess);
    }

    onCancelCommentForm();

    event.stopPropagation();
  }, [dealTaskId, commentList]);

  const onCommentActiveIdChange: Function = useCallback((
    event: React.ChangeEvent, commentId: number,
  ): void => {
    setCommentActiveId(commentId);

    event.stopPropagation();
  }, []);

  const closeModalByClickAway: Function = useCallback((): void => {
    if (!isSaveDisabled && (isNewEntity || isModalEditMode) && !isEditMode) {
      setIsLeaveConfirmModalOpen(true);
    } else {
      if (isModalEditMode && onCancel) {
        onCancel();
      }

      if (onCloseModal) {
        onCloseModal();
      }

      cancelForm();
    }
  }, [isSaveDisabled, isNewEntity, isModalEditMode, isEditMode]);

  const closeLeaveConfirmModal: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(false);
  }, []);

  const onLeaveConfirm: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(false);

    if (onCancel) {
      onCancel();
    }

    if (onCloseModal) {
      onCloseModal();
    }

    cancelForm();
  }, []);

  const reviewButtonClick: MouseEventHandlerFunction = useCallback((): void => {
    if (onReviewButtonClick) {
      onReviewButtonClick(currentUserOpenReviewId, DEAL_TASK_REVIEW_STATUS_APPROVED);
    }
  }, [currentUserOpenReviewId]);

  const onCommentAttachmentFileOpenActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setFileMenuAnchor(event.currentTarget);
  }, []);

  const onCheckIsFileMenuItemVisible: Function = useCallback((
    actionMenuItem: ActionMenuItem, isAnalyzable: string | null,
  ): boolean => (
    ((actionMenuItem.isVisibleByEntityTypeValue && isAnalyzable && FormatService.extractBooleanValue(isAnalyzable))
      || !actionMenuItem.isVisibleByEntityTypeValue)
  ), []);

  const closeFileActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setFileMenuAnchor(null);

    event.stopPropagation();
  }, []);

  const checkIsFileAnalyzable: Function = useCallback((file?: FileItem): boolean => {
    return !!file && !!file.mimeType && file.mimeType === MIME_TYPE_APPLICATION_PDF;
  }, []);

  const onContactClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    contactId: number,
  ): void => {
    navigate({
      pathname: `${ROUTE_CONTACT}/${contactId}`,
    });

    event.stopPropagation();
  }, []);

  const menuItemDealTaskStatusList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: dealTaskStatusList,
    onClick: !isEditMode && !isModalEditMode ? ((
      event: React.MouseEvent<Element, MouseEvent>, selectedStatus: DealTaskStatus,
    ): void => {
      if (selectedStatus.name) {
        onStatusSelect(selectedStatus.name);
      }

      closeStatusMenu(event);
    }) : undefined,
  }), [dealTaskStatusList, isEditMode, isModalEditMode, isNewEntity, isNewDeal]);

  const menuItemDealTaskGroupList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemLabelKey: 'name',
    optionList: dealTaskGroupList,
  }), [dealTaskGroupList]);

  const menuItemFilteredDealTaskPlainList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemLabelKey: 'name',
    optionList: filteredDealTaskPlainList,
  }), [filteredDealTaskPlainList]);

  const menuItemDealTaskContactPersonList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionItemExtraLabelKey: 'email',
    optionList: dealTaskContactPersonList,
    defaultImage: PersonDefaultImage,
  }), [dealTaskContactPersonList]);

  const menuItemDealTaskReviewContactList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionList: dealTaskContactPersonList,
    defaultImage: PersonDefaultImage,
    selectedItemList: selectedDealTaskReviewContacts,
  }), [dealTaskContactPersonList, selectedDealTaskReviewContacts]);

  const menuItemAccessLevelList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: accessLevelList,
  }), [accessLevelList]);

  useEffect((): void => {
    if (dealTaskGroupId) {
      setCurrentDealTaskGroupId(dealTaskGroupId);
    }
  }, [dealTaskGroupId]);

  useEffect((): ReactUseEffectCleanUp => {
    const timeoutId: TimeoutId | null = setTimeout((): void => {
      if (contentContainerRef && contentContainerRef.current) {
        const contentContainer: ChildNode | null = (contentContainerRef.current as HTMLElement).firstChild;

        if (contentContainer) {
          setContentHeight((contentContainer as HTMLElement).clientHeight);
        }
      }
    }, TIMEOUT_DEFAULT);

    return (): void => {
      clearTimeout(timeoutId);
    };
  }, [contentContainerRef, isOpen, attachmentList]);

  useEffect((): void => {
    if (dealTaskData) {
      setSelectedDealTaskReviewContacts(dealTaskData.reviews && dealTaskData.reviews.length
        ? dealTaskData.reviews.map((dealTaskReviewItem: DealTaskReview): Contact => dealTaskReviewItem.contact) : []);
    }
  }, [dealTaskId]);

  return (
    <Fragment>
      {
        (!!dealTaskData || isNewEntity) && (
          <ModalLayout
            isOpen={isOpen}
            isMedium={!isFullWidthTable}
            isModalEditMode={isModalEditMode}
            withEditModeSwitcher={withEditModeSwitcher}
            title={modalTitle}
            editPermission={DEAL_TASK_EDIT}
            onCloseModal={cancelChangesAndCloseModal}
            onCloseModalByClickAway={closeModalByClickAway}
            onSwitchModalEditMode={onSwitchModalEditMode}
          >
            <Form>
              <Grid withSmallSpacing>
                <GridItem isFullWidth={isFullWidthTable} isMediumWidth={!isFullWidthTable}>
                  <Card isContainer isFullHeight withNoPadding>
                    <Table>
                      <TableBody isPlain withNoBorders withTwoColumns withHeaderColumn>
                        {
                          !isNewEntity && !isNewDeal && (
                            <TableRow>
                              <TableCell>
                                Status
                                {
                                  isEditMode && isModalEditMode && (
                                    <RequiredMark />
                                  )
                                }
                              </TableCell>
                              <TableCell>
                                {
                                  isEditMode || isModalEditMode ? (
                                    <FormFieldSelect
                                      isBordered
                                      isDisabled={dealTaskStatusList && !dealTaskStatusList.length}
                                      name={`${formPrefix}.status`}
                                      optionsTotal={dealTaskStatusList && dealTaskStatusList.length}
                                    >
                                      {menuItemDealTaskStatusList}
                                    </FormFieldSelect>
                                  ) : (
                                    <FlexContainer>
                                      <TextBlock>
                                        <DealTaskStatusIcon withRightOffset dealTaskData={dealTaskData} />
                                      </TextBlock>
                                      <TextBlock>
                                        {selectedStatusText || (dealTaskData && dealTaskData.statusText)}
                                      </TextBlock>
                                      <CheckAccess permissions={dealTaskStatusEditPermissions}>
                                        <DropdownButton onClick={openStatusMenu} />
                                        <Menu menuAnchor={menuAnchor} onClose={onCloseStatusMenu}>
                                          {menuItemDealTaskStatusList}
                                        </Menu>
                                      </CheckAccess>
                                    </FlexContainer>
                                  )
                                }
                              </TableCell>
                            </TableRow>
                          )
                        }
                        {
                          (((isEditMode || isNewEntity || isNewDeal || isModalEditMode) && !isNewGroupVisible)
                            || (!isEditMode && !isNewEntity && !isNewDeal && !isModalEditMode)) && (
                            <TableRow>
                              <TableCell>
                                Group
                                {
                                  (isEditMode || isNewDeal || isNewEntity || isModalEditMode) && (
                                    <RequiredMark />
                                  )
                                }
                              </TableCell>
                              <TableCell>
                                {
                                  isEditMode || isNewEntity || isNewDeal || isModalEditMode ? (
                                    <FormFieldSelect
                                      isBordered
                                      withBlankElement
                                      isDisabled={(isNewEntity && dealTaskGroupId)
                                        || (dealTaskData && !dealTaskData.name)}
                                      name={`${formPrefix}.groupId`}
                                      optionsTotal={dealTaskGroupList && dealTaskGroupList.length}
                                      onChange={((dealTaskGroupId: number): void => {
                                        dealTaskGroupChange(dealTaskGroupId);
                                      })}
                                      newItemComponent={(
                                        !isNewGroupVisible && (
                                          <MenuItemAddNewItem
                                            label="New Group"
                                            createPermission={DEAL_TASK_GROUP_CREATE}
                                            onClick={openNewGroupForm}
                                          />
                                        )
                                      )}
                                    >
                                      {menuItemDealTaskGroupList}
                                    </FormFieldSelect>
                                  ) : (dealTaskGroupData && dealTaskGroupData.name)
                                }
                              </TableCell>
                            </TableRow>
                          )
                        }
                        {
                          (isEditMode || isNewEntity || isNewDeal || isModalEditMode) && isNewGroupVisible && (
                            <TableRow>
                              <TableCell>
                                Group (new)
                              </TableCell>
                              <TableCell isTextRight>
                                <ActionButton onClick={closeNewGroupForm}>
                                  <CancelFilledIcon />
                                </ActionButton>
                              </TableCell>
                            </TableRow>
                          )
                        }
                        {
                          (isEditMode || isNewEntity || isNewDeal || isModalEditMode) && isNewGroupVisible && (
                            <TableRow>
                              <TableCell>
                                Group Name
                                <RequiredMark />
                              </TableCell>
                              <TableCell>
                                <FormFieldText isBordered name={`${formPrefix}.group.name`} />
                              </TableCell>
                            </TableRow>
                          )
                        }
                        {
                          (isEditMode || isNewEntity || isNewDeal || isModalEditMode) && (
                            <TableRow>
                              <TableCell>
                                Parent Task
                              </TableCell>
                              <TableCell>
                                <FormFieldSelect
                                  isBordered
                                  withBlankElement
                                  isDisabled={menuItemFilteredDealTaskPlainList
                                    && !menuItemFilteredDealTaskPlainList.length}
                                  name={`${formPrefix}.parentId`}
                                  optionsTotal={filteredDealTaskPlainList && filteredDealTaskPlainList.length}
                                >
                                  {menuItemFilteredDealTaskPlainList}
                                </FormFieldSelect>
                              </TableCell>
                            </TableRow>
                          )
                        }
                        {
                          (isEditMode || isNewEntity || isNewDeal || isModalEditMode) && (
                            <TableRow>
                              <TableCell>
                                Name
                                <RequiredMark />
                              </TableCell>
                              <TableCell>
                                <FormFieldText isBordered name={`${formPrefix}.name`} />
                              </TableCell>
                            </TableRow>
                          )
                        }
                        {
                          ((isEditMode || isNewEntity || isNewDeal || isModalEditMode)
                            || (!isEditMode && !isNewEntity && !isNewDeal && !isModalEditMode && dueDate)) && (
                            <TableRow>
                              <TableCell>
                                Due Date
                              </TableCell>
                              <TableCell>
                                {
                                  isEditMode || isNewEntity || isNewDeal || isModalEditMode ? (
                                    <FormFieldDate
                                      isBordered
                                      withBlankValues
                                      name={`${formPrefix}.dueDate`}
                                    />
                                  ) : dueDate
                                }
                              </TableCell>
                            </TableRow>
                          )
                        }
                        {
                          (
                            ((isEditMode || isNewEntity || isNewDeal || isModalEditMode)
                              || (!isEditMode && !isNewEntity && !isNewDeal
                                && !isModalEditMode && (dealTaskData && dealTaskData.contact)))
                          ) && (
                            <TableRow>
                              <TableCell>
                                Assigned To
                              </TableCell>
                              <TableCell>
                                {
                                  isEditMode || isNewEntity || isNewDeal || isModalEditMode ? (
                                    <FormFieldSelect
                                      isBordered
                                      withSearch
                                      withBlankElement
                                      isDisabled={dealTaskContactPersonList && !dealTaskContactPersonList.length}
                                      blankElementLabel="Unassigned"
                                      name={`${formPrefix}.contactId`}
                                      optionsTotal={dealTaskContactPersonList && dealTaskContactPersonList.length}
                                      onSearch={onDealTaskContactPersonSearch}
                                      onClose={onDealTaskContactPersonSearchClose}
                                    >
                                      {menuItemDealTaskContactPersonList}
                                    </FormFieldSelect>
                                  ) : (
                                    dealTaskData && dealTaskData.contact && (
                                      <FilteredLink
                                        withNewWindow
                                        link={`${ROUTE_CONTACT}/${dealTaskData.contact.id}`}
                                        permission={CONTACT_VIEW}
                                      >
                                        {dealTaskData.contact.fullName}
                                      </FilteredLink>
                                    )
                                  )
                                }
                              </TableCell>
                            </TableRow>
                          )
                        }
                        {
                          !isEditMode && !isNewEntity && !isNewDeal && !isModalEditMode && dealTaskData && (
                            <TableRow>
                              <TableCell>
                                Date Created
                              </TableCell>
                              <TableCell>
                                {dealTaskData.createdDate}
                              </TableCell>
                            </TableRow>
                          )
                        }
                        {
                          (isEditMode || isNewEntity || isNewDeal || isModalEditMode) && (
                            <CheckAccess permissions={dealTaskAccessLevelViewPermissions}>
                              <TableRow>
                                <TableCell>
                                  Access Limit
                                </TableCell>
                                <TableCell>
                                  <FormFieldSelect
                                    isBordered
                                    withBlankElement
                                    isDisabled={accessLevelList && !accessLevelList.length}
                                    blankElementLabel="Everyone"
                                    name={`${formPrefix}.accessLevel`}
                                    optionsTotal={accessLevelList && accessLevelList.length}
                                  >
                                    {menuItemAccessLevelList}
                                  </FormFieldSelect>
                                </TableCell>
                              </TableRow>
                            </CheckAccess>
                          )
                        }
                        {
                          (isEditMode || isNewEntity || isNewDeal || isModalEditMode
                            || (dealTaskData.reviews && !!dealTaskData.reviews.length)) && (
                            <TableRow>
                              <TableCell>
                                {isEditMode || isNewEntity || isNewDeal || isModalEditMode ? 'Reviewers' : 'Reviews'}
                              </TableCell>
                              <TableCell>
                                {
                                  (isEditMode || isNewEntity || isNewDeal || isModalEditMode) ? (
                                    <FormFieldMultiSelect
                                      withImage
                                      isBordered
                                      isDisabled={dealTaskContactPersonList && !dealTaskContactPersonList.length}
                                      name={`${formPrefix}.reviewContactIds`}
                                      nameKey="fullName"
                                      defaultImage={PersonDefaultImage}
                                      optionList={dealTaskContactPersonList}
                                      onValuesChange={dealTaskReviewContactsChange}
                                    >
                                      {menuItemDealTaskReviewContactList}
                                    </FormFieldMultiSelect>
                                  ) : (
                                    dealTaskData.reviews.map((dealTaskReviewItem: DealTaskReview): ReactElement => (
                                      <FlexContainer key={dealTaskReviewItem.id}>
                                        {
                                          dealTaskReviewItem.contact && (
                                            <ChipLink
                                              isDisabled={isEditMode}
                                              label={dealTaskReviewItem.contact.fullName}
                                              imageLink={dealTaskReviewItem.contact.photoThumbnail
                                                || dealTaskReviewItem.contact.photo || PersonDefaultImage}
                                              permission={CONTACT_VIEW}
                                              onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                                onContactClick(event, dealTaskReviewItem.contact.id);
                                              }}
                                            />
                                          )
                                        }
                                        {
                                          dealTaskReviewItem.status === DEAL_TASK_STATUS_APPROVED && (
                                            <div>
                                              <DealTaskReviewStatusIcon dealTaskReview={dealTaskReviewItem} />
                                            </div>
                                          )
                                        }
                                      </FlexContainer>
                                    ))
                                  )
                                }
                                {
                                  isReviewRequired && !isEditMode && !isNewEntity && !isNewDeal
                                  && !isModalEditMode && (
                                    <FlexContainer>
                                      <div>
                                        This task requires your approval
                                      </div>
                                      <div>
                                        <Button isActive isNarrow onClick={reviewButtonClick}>
                                          Approve
                                        </Button>
                                      </div>
                                    </FlexContainer>
                                  )
                                }
                              </TableCell>
                            </TableRow>
                          )
                        }
                        {
                          !isEditMode && !isNewEntity && !isNewDeal && !isModalEditMode
                          && attachmentList && !!attachmentList.length && (
                            <CheckAccess permissions={dealTaskAttachmentListViewPermissions}>
                              <TableRow>
                                <TableCell>
                                  Documents
                                </TableCell>
                                <TableCell>
                                  <FileListContainer isLimited>
                                    {
                                      attachmentList.map((attachmentItem: Attachment): ReactElement => (
                                        <FlexContainer isInline key={attachmentItem.id}>
                                          <FileLink
                                            withActionMenu={!isEditMode && !isNewDeal}
                                            fileLink={attachmentItem.file}
                                            actionProps={{
                                              [DATA_ATTACHMENT_ID]: attachmentItem.id,
                                              [DATA_IS_ANALYZABLE]: checkIsFileAnalyzable(attachmentItem.file)
                                                ? FormatService.BOOLEAN_VALUE_TRUE : null,
                                            }}
                                            actionMenuItems={fileActionMenuItems}
                                            actionMenuPermissions={attachmentActionMenuPermissions}
                                            entityTypeProp={DATA_IS_ANALYZABLE}
                                            onMouseDown={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                              onCommentAttachmentFileViewClick(
                                                event,
                                                attachmentItem.file,
                                                dealTaskData.name,
                                                dealTaskGroupData.name,
                                              );
                                            }}
                                            onOpenActionMenu={onCommentAttachmentFileOpenActionMenu}
                                            onCheckIsMenuItemVisible={onCheckIsFileMenuItemVisible}
                                          />
                                        </FlexContainer>
                                      ))
                                    }
                                  </FileListContainer>
                                </TableCell>
                              </TableRow>
                            </CheckAccess>
                          )
                        }
                      </TableBody>
                    </Table>
                  </Card>
                </GridItem>
                {
                  !isEditMode && !isNewEntity && !isNewDeal && !isModalEditMode && (
                    <CheckAccess permissions={dealTaskCommentListViewPermissions}>
                      <GridItem isFullWidth={isSmallScreen} isMediumWidth={!isSmallScreen}>
                        <Card isContainer withNoPadding>
                          <CommentTable
                            withAttachmentActionMenu
                            queryCommentId={queryCommentId}
                            commentList={commentList}
                            commentEditId={commentEditId}
                            commentActiveId={commentActiveId}
                            currentUser={currentUser}
                            contentHeight={contentHeight}
                            editPermissions={dealTaskCommentEditPermissions}
                            deletePermissions={dealTaskCommentDeletePermissions}
                            mentionPermission={CONTACT_VIEW}
                            hashtagPermission={SEARCH_RESULT_VIEW}
                            attachmentActionMenuPermissions={dealTaskAttachmentActionMenuPermissions}
                            attachmentActionMenuItems={fileActionMenuItems}
                            onSwitchCommentEditMode={switchCommentEditMode}
                            onDeleteCommentConfirm={onDeleteCommentConfirm}
                            onDeleteCommentCancel={onDeleteCommentCancel}
                            onCommentEntityLinkClick={onCommentEntityLinkClick}
                            onCommentActiveIdChange={onCommentActiveIdChange}
                            onCommentAttachmentFileViewClick={(
                              event: React.MouseEvent<Element, MouseEvent>, attachmentFile: FileItem,
                            ): void => {
                              onCommentAttachmentFileViewClick(
                                event,
                                attachmentFile,
                                dealTaskData.name,
                                dealTaskGroupData.name,
                              );
                            }}
                            onCommentAttachmentFileOpenActionMenu={onCommentAttachmentFileOpenActionMenu}
                            onCheckIsCommentAttachmentFileMenuItemVisible={onCheckIsFileMenuItemVisible}
                          />
                        </Card>
                      </GridItem>
                    </CheckAccess>
                  )
                }
              </Grid>
              {
                !isEditMode && !isNewEntity && !isNewDeal && !isModalEditMode && (
                  <CheckAccess permissions={dealTaskCommentCreatePermissions}>
                    <CommentForm
                      isCommentSaving={isCommentSaving}
                      isCommentFormActive={isCommentFormActive}
                      attachmentFiles={commonAttachmentFiles}
                      commentText={commentText}
                      mentionList={mentionList}
                      hashtagList={hashtagList}
                      mentionRouteRoot={ROUTE_CONTACT}
                      hashtagRouteRoot={ROUTE_SEARCH}
                      commentAttachmentDropDeps={commentAttachmentDropDeps}
                      onCommentFieldChange={onCommentFieldChange}
                      onCommentFieldKeyDown={onCommentFieldKeyDown}
                      onSaveComment={saveComment}
                      onCommentAttachmentFilesChange={onCommentAttachmentFilesChange}
                      onCommentAttachmentFileDelete={onCommentAttachmentFileDelete}
                      onCommentAttachmentFileError={onCommentAttachmentFileError}
                    />
                  </CheckAccess>
                )
              }
              {
                isModalEditMode ? (
                  <div>
                    {
                      isSaving && (
                        <Loader isInline isFloating isModal isSubmit />
                      )
                    }
                    <ControlButtonBlock
                      isModal
                      isModalEditMode={isModalEditMode}
                      isNewEntity={isNewEntity}
                      isSaveDisabled={isSaveDisabled}
                      withOffset={isRightDrawerOpen}
                      onCancelForm={onCancelForm}
                      onSubmitForm={onSubmitForm}
                    />
                  </div>
                ) : (
                  <ModalButtonBlock>
                    {
                      (selectedStatus || commentText || isNewEntity
                        || (commentAttachmentFiles && !!commentAttachmentFiles.length)) && (
                        <Button onClick={cancelChangesAndCloseModal}>
                          Cancel
                        </Button>
                      )
                    }
                    <Button
                      isDisabled={isSaving}
                      isActive={!!selectedStatus || !!commentText || isNewEntity
                        || (commentAttachmentFiles && !!commentAttachmentFiles.length)}
                      onClick={applyChangesAndCloseModal}
                    >
                      {selectedStatus || commentText || isNewEntity
                      || (commentAttachmentFiles && commentAttachmentFiles.length) ? 'Save' : 'Close'}
                    </Button>
                    {
                      isSaving && !isSmallScreen && (
                        <Loader isInline isModal isSmall />
                      )
                    }
                  </ModalButtonBlock>
                )
              }
            </Form>
            <LeaveConfirmModal
              isSubModal
              isOpen={isLeaveConfirmModalOpen}
              onConfirm={onLeaveConfirm}
              onCloseModal={closeLeaveConfirmModal}
            />
          </ModalLayout>
        )
      }
      <ContextMenu
        menuAnchor={fileMenuAnchor}
        actionMenuItems={fileActionMenuItems}
        dataAttributeEntityId={DATA_ATTACHMENT_ID}
        dataAttributeEntityType={DATA_IS_ANALYZABLE}
        onClose={closeFileActionMenu}
        onCheckIsMenuItemVisible={onCheckIsFileMenuItemVisible}
      />
      <StatusBar isOpen={isOpenMessage} isError={isMessageError} message={statusMessage} onClose={closeMessage} />
    </Fragment>
  );
});

export default DealTaskModal;
